/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import { types, applySnapshot, getSnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';
import * as constants from '../../constants/ServiceConstants';
import * as commonConstants from '../../../../common/constants';
import AdvancedSettingsStateModel, { defaultAdvancedSettingsState } from './AdvancedSettingsStateModel';

// Settings model for graph configuration
const GraphSettingsModel = types.model('GraphSettingsModel', {
  skipHistory: types.optional(types.boolean, false),
  skipContentFiltering: types.optional(types.boolean, false),
  showAdvancedSettings: types.optional(types.boolean, false),
  // Graph context - used by Instructions/AdvancedSettings
  currentGraphId: types.maybeNull(types.string),
  currentTenantId: types.maybeNull(types.string),
  currentGraphStatus: types.optional(types.string, ''),
  // Eval summaries from GET graph — [{ rubricId, rubricName }] for Graph Tester
  evaluations: types.optional(types.frozen(), []),
  // Mapped from graph.rubricData on load; legacy single-eval cache
  evaluationCriteria: types.maybeNull(types.frozen()),
  // Other custom parameters (JSON object)
  otherParams: types.optional(types.frozen(), {})
});

const GraphModel = types
  .model('GraphModel', {
    // Graph data
    graphData: types.maybeNull(types.frozen()),

    // Graph settings for runtime configuration
    settings: types.optional(GraphSettingsModel, {}),

    // Advanced Settings modal state (form fields, JSON editor buffer, audio provider list)
    advancedSettingsState: types.optional(AdvancedSettingsStateModel, defaultAdvancedSettingsState),

    // Flag to show/hide history icon
    historyExists: types.optional(types.boolean, false),

    // Optional error for consumers that want to render error state from the store
    // (loading/error booleans should come from Greenville status stores like `.isPending` / `.isError`)
    error: types.optional(types.maybeNull(types.string), null)
  })
  .actions((self) => ({
    // Existing actions
    deleteGraph(payload) {
      Framework.getEventManager().publish(constants.DELETE_GRAPH, { payload });
    },

    getGraphAccess(payload) {
      Framework.getEventManager().publish(constants.GET_GRAPH_ACCESS, { payload });
    },

    updateGraphTitle(payload) {
      Framework.getEventManager().publish(constants.UPDATE_GRAPH_TITLE, { payload });
    },

    updateGraphAccess(payload) {
      Framework.getEventManager().publish(constants.UPDATE_GRAPH_ACCESS, { payload });
    },

    // New actions for graph operations
    getGraph(payload) {
      Framework.getEventManager().publish(constants.GET_GRAPH, { payload });
    },

    setGraphData(data) {
      applySnapshot(self, {
        ...getSnapshot(self),
        graphData: data,
        error: null
      });
    },

    clearGraphData() {
      applySnapshot(self, {
        ...getSnapshot(self),
        graphData: null
      });
    },

    setError(message) {
      applySnapshot(self, {
        ...getSnapshot(self),
        error: message || null
      });
    },

    clearError() {
      applySnapshot(self, {
        ...getSnapshot(self),
        error: null
      });
    },

    // Separate graph operations - each maps to its own service
    createGraph(payload) {
      Framework.getEventManager().publish(constants.CREATE_GRAPH, { payload });
    },

    updateGraph(payload) {
      Framework.getEventManager().publish(constants.UPDATE_GRAPH, { payload });
    },

    createGraphVersion(payload) {
      Framework.getEventManager().publish(constants.CREATE_GRAPH_VERSION, { payload });
    },

    publishGraph(payload) {
      Framework.getEventManager().publish(constants.PUBLISH_GRAPH, { payload });
    },

    promoteGraph(payload) {
      Framework.getEventManager().publish(constants.PROMOTE_GRAPH, { payload });
    },

    copyGraph(payload) {
      Framework.getEventManager().publish(constants.COPY_GRAPH, { payload });
    },

    setGraphDefault(payload) {
      Framework.getEventManager().publish(constants.SET_GRAPH_DEFAULT, { payload });
    },

    saveEvaluationRubricText(payload) {
      Framework.getEventManager().publish(constants.SAVE_EVALUATION_RUBRIC_TEXT, { payload });
    },

    createEvaluation(payload) {
      Framework.getEventManager().publish(constants.CREATE_EVALUATION, { payload });
    },

    listGraphEvaluations(payload) {
      Framework.getEventManager().publish(constants.GET_GRAPH_EVALUATIONS, { payload });
    },

    deleteEvaluation(payload) {
      Framework.getEventManager().publish(constants.DELETE_EVALUATION, { payload });
    },

    saveEvaluationScheduler(payload) {
      Framework.getEventManager().publish(constants.SAVE_EVALUATION_SCHEDULER, { payload });
    },

    // Book titles - uses existing aiadmin GetBookTitlesService
    getBookTitles(payload) {
      // Format payload for aiadmin service which expects apiConfig.identifierSource
      Framework.getEventManager().publish(commonConstants.FETCH_BOOK_TITLES_DATA, {
        payload: {
          apiConfig: { identifierSource: payload.identifierSource },
          tenantId: payload.tenantId
        }
      });
    },

    // Reset all graph-related state
    resetGraphState() {
      applySnapshot(self, {
        graphData: null,
        settings: {
          skipHistory: false,
          skipContentFiltering: false,
          showAdvancedSettings: false,
          currentGraphId: null,
          currentTenantId: null,
          currentGraphStatus: '',
          evaluations: [],
          evaluationCriteria: null,
          otherParams: {}
        },
        advancedSettingsState: defaultAdvancedSettingsState,
        historyExists: false,
        error: null
      });
    },

    // Settings actions - using applySnapshot to satisfy ESLint no-param-reassign
    setSkipHistory(value) {
      applySnapshot(self.settings, {
        ...getSnapshot(self.settings),
        skipHistory: value
      });
    },

    setSkipContentFiltering(value) {
      applySnapshot(self.settings, {
        ...getSnapshot(self.settings),
        skipContentFiltering: value
      });
    },

    setShowAdvancedSettings(value) {
      applySnapshot(self.settings, {
        ...getSnapshot(self.settings),
        showAdvancedSettings: value
      });
    },

    setOtherParams(value) {
      applySnapshot(self.settings, {
        ...getSnapshot(self.settings),
        otherParams: value || {}
      });
    },

    initAdvancedSettingsState() {
      const featureConfigModel = Framework.getStoreRegistry().getStore('featureConfigModel');
      const tenantMemorySaverEnabled = featureConfigModel?.isTenantMemorySaverEnabled ?? false;
      const tenantAgentThinkingEnabled = featureConfigModel?.isTenantAgentThinkingEnabled ?? false;
      self.advancedSettingsState.initFromOtherParams(
        self.settings.otherParams,
        tenantMemorySaverEnabled,
        tenantAgentThinkingEnabled
      );
    },

    // Bulk update settings (useful when loading graph data)
    updateSettings(settingsObj) {
      if (settingsObj) {
        const currentSettings = getSnapshot(self.settings);
        const newSettings = { ...currentSettings };

        if (typeof settingsObj.skipHistory === 'boolean') {
          newSettings.skipHistory = settingsObj.skipHistory;
        }
        if (typeof settingsObj.skipContentFiltering === 'boolean') {
          newSettings.skipContentFiltering = settingsObj.skipContentFiltering;
        }
        if (typeof settingsObj.showAdvancedSettings === 'boolean') {
          newSettings.showAdvancedSettings = settingsObj.showAdvancedSettings;
        }
        if (settingsObj.currentGraphId !== undefined) {
          newSettings.currentGraphId = settingsObj.currentGraphId;
        }
        if (settingsObj.currentTenantId !== undefined) {
          newSettings.currentTenantId = settingsObj.currentTenantId;
        }
        if (settingsObj.currentGraphStatus !== undefined) {
          newSettings.currentGraphStatus = settingsObj.currentGraphStatus;
        }
        if (settingsObj.evaluations !== undefined) {
          newSettings.evaluations = Array.isArray(settingsObj.evaluations) ? settingsObj.evaluations : [];
        }
        if (settingsObj.otherParams !== undefined) {
          newSettings.otherParams = settingsObj.otherParams;
        }
        if (settingsObj.evaluationCriteria !== undefined) {
          newSettings.evaluationCriteria = settingsObj.evaluationCriteria;
        }

        applySnapshot(self.settings, newSettings);
      }
    },
    setHistoryExists(value) {
      applySnapshot(self, {
        ...getSnapshot(self),
        historyExists: value
      });
    },

    // Reset settings to defaults
    resetSettings() {
      applySnapshot(self.settings, {
        skipHistory: false,
        skipContentFiltering: false,
        showAdvancedSettings: false,
        currentGraphId: null,
        currentTenantId: null,
        currentGraphStatus: '',
        evaluations: [],
        evaluationCriteria: null,
        otherParams: {}
      });
      self.advancedSettingsState.reset();
    }
  }));

export default GraphModel;
